import type { AutocompleteProps, TextFieldProps } from '@mui/material';
import { Autocomplete, TextField } from '@mui/material';
import { useController } from 'react-hook-form';

import type { FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';

interface AutocompleteInputProps<
  TValue,
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends Omit<AutocompleteProps<TValue, Multiple, DisableClearable, FreeSolo>, 'defaultValue' | 'renderInput'>,
    UseControllerProps<TFieldValues, TName> {
  TextFieldProps?: TextFieldProps;
  options: TValue[];
  renderInput?: AutocompleteProps<TValue, Multiple, DisableClearable, FreeSolo>['renderInput'];
}

export const AutocompleteInput = <
  TValue,
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>({
  name,
  control,
  rules,
  defaultValue,
  onChange,
  TextFieldProps,
  ...restAutocompleteProps
}: AutocompleteInputProps<TValue, TFieldValues, TName, Multiple, DisableClearable, FreeSolo>) => {
  const {
    field,
    fieldState: { error }
  } = useController({
    name,
    control,
    rules,
    defaultValue
  });

  return (
    <Autocomplete
      {...field}
      onChange={(event, data, reason, details) => {
        field.onChange(data);
        onChange?.(event, data, reason, details);
      }}
      {...restAutocompleteProps}
      renderInput={
        restAutocompleteProps.renderInput ||
        (props => {
          return (
            <TextField
              {...props}
              error={!!error}
              variant="outlined"
              color="secondary"
              {...TextFieldProps}
              helperText={error?.message || TextFieldProps?.helperText}
              InputProps={{
                ...TextFieldProps?.InputProps,
                ...props.InputProps,
                ...(TextFieldProps?.InputProps?.startAdornment || props.InputProps.startAdornment
                  ? {
                      startAdornment: (
                        <>
                          {TextFieldProps?.InputProps?.startAdornment}
                          {props.InputProps.startAdornment}
                        </>
                      )
                    }
                  : null)
              }}
            />
          );
        })
      }
    />
  );
};
